import {
  createPlaceholderUser,
  userInjectionKey,
  type ID,
  type MutationCallbacks,
  generateVirtualId,
  guest,
} from '#shared'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { inject, unref } from 'vue'
import { editMessage } from '../api/edit-message'
import type { EditMessagePayload } from '../model/edit-message-payload'
import type { Message } from '../model/message'
import type { SendMessagePayload } from '../model/send-message-payload'
import type { SendMessageResponse } from '../model/send-message-response'
import { updateMessage } from './update-message'
import { updateMessagesByChatId } from './update-messages-by-chat-id'

type SendMessageMutationCallbacks = MutationCallbacks<SendMessagePayload, SendMessageResponse>

export function useEditMessageMutation(chatId: ID, callbacks: SendMessageMutationCallbacks = {}) {
  const queryClient = useQueryClient()

  const currentUser = inject(userInjectionKey)

  const mutation = useMutation({
    ...callbacks,

    mutationFn: (payload: EditMessagePayload) => editMessage(chatId, payload),

    onMutate: payload => {
      const currentUserVal = unref(currentUser)
      if (currentUserVal === guest) return

      const optimisticAttachments = payload.attachments?.map(attachment => ({
        ...attachment,
        id: generateVirtualId(),
      }))

      const isRespondForMessage = payload.respondFor && 'sender' in payload.respondFor

      const optimisticMessage: Message = {
        id: payload.id,
        clientId: payload.clientId,
        chatId,
        respondFor: isRespondForMessage ? (payload.respondFor as Message) : null,
        sender: currentUserVal ?? createPlaceholderUser(),
        text: payload.text,
        attachments: optimisticAttachments ?? [],
        reactions: [],
        deletedAt: null,
        createdAt: new Date().toISOString(),
        isBBCode: payload.isBBCode,
        meta: {
          isSending: true,
          isEdited: true,
        },
      }

      updateMessage(queryClient, optimisticMessage)

      callbacks.onMutate?.(payload)
    },

    onError: (error, payload) => {
      updateMessagesByChatId(queryClient, chatId, message => {
        if (message.clientId !== payload.clientId) return message

        return {
          ...message,
          meta: {
            ...message.meta,
            isSending: false,
            isErrored: true,
            isEdited: true,
          },
        }
      })

      callbacks.onError?.(error, payload)
    },

    onSuccess: (response, payload) => {
      response.data.meta = {
        ...response.data.meta,
        isEdited: true,
      }

      response.data.clientId = payload.clientId

      updateMessage(queryClient, response.data)

      callbacks.onSuccess?.(response, payload)
    },
  })

  return mutation
}
